// ── Bulk import (Wave 5 — REBUILT as a gold-pattern per-screen page) ────────
// The thin fixture "dry-run" surface (hard-coded rows + a read-only CSV textarea)
// was replaced by the real BulkImportPage: a cohort-scoped file → client-side
// preview/validation → confirm → AUTHORITATIVE backend result flow on the gold
// pattern (useHonestQuery -> <HonestState>, @vjal/ui only, idempotency-keyed
// mutation via the existing useCohortLifecycleMutations.bulkImport). The contract
// factory below preserves the route registration + provenance metadata and
// renders the new page via `customBody`.
//
// ── CONTRACT CHECK #1 (bulk shape): there is NO server preview endpoint and the
//    live POST /cohorts/:id/participants/bulk is ALL-OR-NOTHING (throws
//    CO_VALIDATION_FAILED on the first dup/invalid/active-email row). The richer
//    per-row shape is mock-only. So the preview + duplicate/invalid/empty/bad-
//    column detection is CLIENT-SIDE; the confirmed import renders whatever the
//    backend authoritatively returns (count, or per-row results when present).
//
// ── CONTRACT CHECK #2 (Week-3 cutoff): the backend does NOT enforce the cutoff
//    (no CUTOFF_EXCEEDED / week-3 logic anywhere in vjal-coaching-api). The coach
//    add/import cutoff is therefore a UI-only, defense-in-depth gate — flagged as
//    an OPEN DEPENDENCY in coach-cutoff.ts. SA/Admin are never cutoff-gated.

import { BulkImportPage } from "./participants/bulk-import/bulk-import-page";
import type { ProductScreenContract } from "./product-route-contracts";

export function bulkParticipantsScreen(): ProductScreenContract {
  return {
    title: () => "Bulk import participants",
    primaryJob:
      "Upload a participant CSV for one cohort, validate every row client-side (file type, empty file, required columns, email format, duplicates), preview the results, then commit only the clean rows — rendering the authoritative backend outcome.",
    secondaryJob:
      "Super admin and admin can import at any time; a coach can import only in an assigned cohort and only through Week 3 (UI cutoff — backend enforcement is an open dependency); company admin, cohort lead, and participant are denied.",
    workTitle: "Bulk import",
    rows: importRows,
    columns: importColumns(),
    states: ["loading", "empty", "permission-denied", "gate-closed", "unavailable", "error"],
    apiFamilies: ["participant-service", "cohort-service", "history-service"],
    roleDeltas:
      "SA/admin import any week; coach imports only in assigned cohorts and only before the Week-3 cutoff (past cutoff → gate-closed, import disabled); company_admin/cohort_lead/participant are denied the import affordance.",
    extensionSlots: ["XLSX parsing (export-to-CSV today)", "server-side preview/cutoff (open dependency)", "field-mapping templates", "invite-after-import"],
    mobilePosture: "File picker, validation summary badges, and the preview table stack with no horizontal overflow.",
    evidenceRequirement:
      "Playwright must prove valid CSV import, missing-column/bad-email/duplicate/empty states, IMPORT_VALIDATION_FAILED row errors with no success toast, the coach Week-3 gate-closed posture, role denials, and mobile no-overflow.",
    surfaceMode: "focused",
    customBody: BulkImportPage,
  };
}

// Representative rows for the route-contract metadata (the live flow is the real
// source; these seed the contract's `rows` so the registry test sees a non-empty
// shape). These are NOT rendered — the real page parses the uploaded CSV.
const importRows = [
  { id: "row-1", row: "1", participant: "Learner Three", email: "learner.three@example.com", result: "ready" },
  { id: "row-2", row: "2", participant: "Learner One", email: "learner@example.com", result: "duplicate" },
  { id: "row-3", row: "3", participant: "Missing Email", email: "missing-email", result: "invalid" },
];

function importColumns() {
  return [
    { key: "row", header: "Row" },
    { key: "participant", header: "Participant" },
    { key: "email", header: "Email" },
    { key: "result", header: "Result" },
  ];
}
